import {
  FormHelperText,
  IconButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { MouseEvent, ReactNode } from 'react';

import { IconName } from '@/components/Icon';

import { Option } from '@/types/components/common';

import { getDataTestId } from '@/utils/browser';

import { Icon } from '../../Icon/Icon';

import { SelectedOption } from './Select.styled';

type SelectValueProps = Readonly<{
  value: unknown;
  defaultValue: unknown;
  optionByValue: Record<string, Option>;
  placeholder?: string;
  isMultiple?: boolean;
  optionsCount: number;
  renderSelectedOption?: (option?: Option | Option[]) => ReactNode;
}>;

export function SelectValue({
  value,
  defaultValue,
  optionByValue,
  placeholder,
  isMultiple = false,
  optionsCount,
  renderSelectedOption,
}: SelectValueProps) {
  const currentValue = value || defaultValue;

  let displayText: string | undefined;
  let displayIcon: IconName | undefined;
  let isPlaceholder = false;

  if (
    !currentValue ||
    (Array.isArray(currentValue) && currentValue.length === 0) ||
    optionsCount === 0
  ) {
    displayText = placeholder;
    isPlaceholder = true;
  } else if (isMultiple && Array.isArray(currentValue)) {
    const selectedOptions = currentValue
      .map(selectedValue => optionByValue[String(selectedValue)])
      .filter(Boolean);
    if (renderSelectedOption) {
      return (
        <SelectedOption {...getDataTestId('select-option-selected')}>
          {renderSelectedOption(selectedOptions)}
        </SelectedOption>
      );
    }
    const labels = currentValue
      .map(
        selectedValue =>
          optionByValue[String(selectedValue)]?.text || String(selectedValue),
      )
      .filter(Boolean)
      .join(', ');
    displayText = labels || placeholder;
    isPlaceholder = !labels;
  } else {
    const currentOption = optionByValue[String(currentValue)] || {};
    if (renderSelectedOption) {
      return (
        <SelectedOption {...getDataTestId('select-option-selected')}>
          {renderSelectedOption(currentOption)}
        </SelectedOption>
      );
    }
    displayIcon = currentOption.icon;
    displayText =
      currentOption.text ||
      (currentOption.value as string) ||
      (!!currentValue && String(currentValue)) ||
      placeholder;
    isPlaceholder = displayText === placeholder;
  }

  return (
    <SelectedOption {...getDataTestId('select-option-selected')}>
      {displayIcon && (
        <ListItemIcon>
          <Icon name={displayIcon} size={20} />
        </ListItemIcon>
      )}
      <ListItemText>
        <Typography
          component="span"
          variant="bodyB2Regular"
          color={isPlaceholder ? 'text.secondary' : undefined}
        >
          {displayText}
        </Typography>
      </ListItemText>
    </SelectedOption>
  );
}

export function SelectHelperText({
  helperText,
  error,
}: Readonly<{
  helperText?: string;
  error?: boolean;
}>): ReactNode {
  if (!helperText) return null;
  if (error) {
    return (
      <FormHelperText error>
        <Stack gap={0.5} component="span">
          <Icon
            name="alert"
            size={20}
            color={(theme: Theme) => theme.palette.error.main}
          />
          {helperText}
        </Stack>
      </FormHelperText>
    );
  }
  return <FormHelperText>{helperText}</FormHelperText>;
}

export function SelectSearchClearButton({
  onClear,
}: Readonly<{
  onClear: () => void;
}>): ReactNode {
  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClear();
  };
  return (
    <IconButton
      {...getDataTestId('select-search-clear')}
      size="small"
      aria-label="clear"
      edge="end"
      onClick={onClick}
    >
      <Icon
        name="close"
        size={16}
        color={(theme: Theme) => theme.palette.neutral[700]}
      />
    </IconButton>
  );
}

export const getSafeSelectValue = ({
  value,
  options,
  isMultiple,
}: {
  value: unknown;
  options: Option[];
  isMultiple?: boolean;
}) => {
  const findMatchingOptionValue = (candidate: unknown) =>
    options.find(option => String(option.value) === String(candidate))?.value;

  if (isMultiple) {
    if (!Array.isArray(value)) return [];

    return value
      .map(findMatchingOptionValue)
      .filter(
        (optionValue): optionValue is Option['value'] =>
          optionValue !== undefined,
      );
  }

  if (value === undefined || value === null || value === '') {
    return value ?? '';
  }

  return findMatchingOptionValue(value) ?? '';
};
